import React, { useEffect, useState } from "react";
import { Select, Input, Button, Spin } from "antd";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { BASE_CRM_URL } from "../../../../components/config/config";

const { TextArea } = Input;

// const SQL_EDITOR_BASE_URL = "http://localhost:3009/accumen/sqlEditor";

const SqlQuery = () => {
  const [sqlFiles, setSqlFiles] = useState([]);
  const [sqlKeys, setSqlKeys] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const [sqlQuery, setSqlQuery] = useState("");

  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingQuery, setLoadingQuery] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Get SQL Files
  const getSqlFiles = async () => {
    try {
      setLoadingFiles(true);

      const response = await fetch(`${BASE_CRM_URL}sqlEditor/getSqlFiles`);

      const data = await response.json();

      if (data?.files) {
        setSqlFiles(data.files);
      }
    } catch (error) {
      console.error("Error fetching SQL files:", error);

      Swal.fire("Error", "Failed to load SQL files", "error");
    } finally {
      setLoadingFiles(false);
    }
  };

  // Get SQL Keys
  const getSqlKeys = async (fileName) => {
    try {
      setLoadingKeys(true);

      const response = await fetch(
        `${BASE_CRM_URL}sqlEditor/getSqlKeys/${encodeURIComponent(fileName)}`,
      );

      const data = await response.json();

      if (data?.keys) {
        setSqlKeys(data.keys);
      } else {
        setSqlKeys([]);
      }
    } catch (error) {
      console.error("Error fetching SQL keys:", error);

      Swal.fire("Error", "Failed to load SQL keys", "error");
    } finally {
      setLoadingKeys(false);
    }
  };

  // Get SQL Query
  const getSqlQuery = async (fileName, key) => {
    try {
      setLoadingQuery(true);

      const response = await fetch(
        `${BASE_CRM_URL}sqlEditor/getSql/${encodeURIComponent(
          fileName,
        )}/${encodeURIComponent(key)}`,
      );

      const data = await response.json();

      if (data?.sql !== undefined) {
        setSqlQuery(data.sql);
      } else {
        setSqlQuery("");
      }
    } catch (error) {
      console.error("Error fetching SQL query:", error);

      Swal.fire("Error", "Failed to load SQL query", "error");
    } finally {
      setLoadingQuery(false);
    }
  };

  // Update SQL Query
  const updateSqlQuery = async () => {
    if (!selectedFile || !selectedKey) {
      Swal.fire("Warning", "Please select file and SQL key", "warning");
      return;
    }

    if (!sqlQuery.trim()) {
      Swal.fire("Warning", "SQL query cannot be empty", "warning");
      return;
    }

    try {
      setUpdating(true);

      const response = await fetch(
        `${BASE_CRM_URL}sqlEditor/updateSql/${encodeURIComponent(
          selectedFile,
        )}/${encodeURIComponent(selectedKey)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sql: sqlQuery,
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data?.success) {
        Swal.fire({
          icon: "success",
          title: "SQL Updated",
          text: "SQL query updated successfully. A backup was created.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire(
          "Error",
          data?.message || "Failed to update SQL query",
          "error",
        );
      }
    } catch (error) {
      console.error("Error updating SQL query:", error);

      Swal.fire("Error", "Failed to update SQL query", "error");
    } finally {
      setUpdating(false);
    }
  };

  // Load files initially
  useEffect(() => {
    getSqlFiles();
  }, []);

  // File selection
  const handleFileChange = (fileName) => {
    setSelectedFile(fileName);
    setSelectedKey(null);
    setSqlKeys([]);
    setSqlQuery("");

    getSqlKeys(fileName);
  };

  // SQL key selection
  const handleKeyChange = (key) => {
    setSelectedKey(key);
    setSqlQuery("");

    getSqlQuery(selectedFile, key);
  };

  return (
    <div>
      <div className="page-wrapper">
        {/* {exportLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Spin size="large" tip="Processing..." />
          </div>
        )} */}
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/crm/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      SQL Editor
                    </h4>
                  </ol>
                </nav>
                <p className="text-muted">Get and Update SQL Queries</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="row">
                {/* SQL File Dropdown */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    SQL File <span className="text-danger">*</span>
                  </label>

                  <Select
                    className="w-100"
                    placeholder="Select SQL File"
                    loading={loadingFiles}
                    value={selectedFile}
                    onChange={handleFileChange}
                    showSearch
                    optionFilterProp="label"
                    options={sqlFiles.map((file) => ({
                      value: file,
                      label: file,
                    }))}
                  />
                </div>

                {/* SQL Key Dropdown */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    SQL Key <span className="text-danger">*</span>
                  </label>

                  <Select
                    className="w-100"
                    placeholder="Select SQL Key"
                    loading={loadingKeys}
                    disabled={!selectedFile}
                    value={selectedKey}
                    onChange={handleKeyChange}
                    showSearch
                    optionFilterProp="label"
                    options={sqlKeys.map((key) => ({
                      value: key,
                      label: key,
                    }))}
                  />
                </div>

                {/* SQL Query */}
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    SQL Query <span className="text-danger">*</span>
                    <small className="text-muted">
                      Note: Table schema must be <code>{"{tableName}"}</code>.
                      For example, if the table name is{" "}
                      <code>accumen_tblitems_groups</code>, enter{" "}
                      <code>{"{tableName}_tblitems_groups"}</code>.
                    </small>
                  </label>

                  {loadingQuery ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: 300 }}
                    >
                      <Spin size="large" />
                    </div>
                  ) : (
                    <TextArea
                      value={sqlQuery}
                      readOnly
                      onChange={(e) => setSqlQuery(e.target.value)}
                      placeholder="SQL query will appear here..."
                      autoSize={{ minRows: 15, maxRows: 30 }}
                      disabled={!selectedKey}
                      style={{
                        fontFamily: "monospace",
                        fontSize: 14,
                        whiteSpace: "pre",
                      }}
                    />
                  )}
                </div>

                {/* Update Button */}
                <div className="col-md-12 text-end d-none">
                  <Button
                    type="primary"
                    onClick={updateSqlQuery}
                    loading={updating}
                    disabled={!selectedFile || !selectedKey || loadingQuery}
                  >
                    Update SQL
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <CommonFooter /> */}
      </div>
    </div>
  );
};

export default SqlQuery;
